import { useEffect } from "react";
import { useAuth } from "@/lib/auth/context";
import { useCompletions } from "./useCompletions";
import { useChoreProposals } from "./useChoreProposals";
import { useChores } from "./useChores";
import { useInstantSubmissions } from "./useInstantSubmissions";
import { useTodaysCompletions } from "./useTodaysCompletions";
import { setAppBadge, clearAppBadge } from "@/lib/utils/appBadge";
import { isChoreActiveToday, isChoreForUser } from "@/lib/utils/chores";

/**
 * Synchronizuje liczbę na ikonie home-screen (Web App Badging API) z
 * realtime stanem aktywności w aplikacji.
 *
 * Logika per rola:
 *  - parent → badge = liczba completions w household ze statusem "pending"
 *  - child  → badge = liczba zadań na dziś jeszcze do zrobienia
 *             (assigned do mnie lub "any", aktywne dziś, brak completion
 *              albo ostatnia odrzucona — czyli to samo co przycisk "Zrób!")
 *  - brak userDoc / niezalogowany → no-op (nie ruszamy stanu OS-a)
 *
 * Pokrywa tylko foreground (gdy app jest otwarta). Gdy app jest zamknięta,
 * badge utrzymuje się na ostatniej wartości aż do następnego push'a —
 * tę aktualizację robi service worker w `firebase-messaging-sw.js` na
 * podstawie `payload.data.badge`.
 */
export function useAppBadgeSync(): void {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const isParent = userDoc?.role === "parent";
  const isChild = userDoc?.role === "child";

  const { completions: pendingCompletions } = useCompletions(
    isParent ? hid : undefined,
    { status: "pending" },
  );
  const { proposals: pendingProposals } = useChoreProposals(
    isParent ? hid : undefined,
    { status: "pending" },
  );
  const { submissions: pendingSubmissions } = useInstantSubmissions(
    isParent ? hid : undefined,
    { status: "pending" },
  );

  // Listenery dla dziecka — i tak mountowane przez DataPrefetcher,
  // tu podpinamy się do tych samych cache'y modułowych.
  const { chores } = useChores(isChild ? hid : undefined);
  const { completions: todays } = useTodaysCompletions(
    isChild ? hid : undefined,
    isChild ? uid : undefined,
  );

  useEffect(() => {
    if (!userDoc) return;
    if (isParent) {
      setAppBadge(
        pendingCompletions.length +
          pendingProposals.length +
          pendingSubmissions.length,
      );
      return;
    }
    if (isChild) {
      if (!uid) return;
      const myChores = chores.filter(
        (c) =>
          c.active &&
          isChoreActiveToday(c) &&
          isChoreForUser(c.assignedTo, uid),
      );
      // Status najświeższy per chore — taka sama logika jak ChildDashboard.
      const statusByChore = new Map<
        string,
        "approved" | "pending" | "rejected"
      >();
      for (const c of todays) {
        const cur = statusByChore.get(c.choreId);
        if (!cur || cur === "rejected") statusByChore.set(c.choreId, c.status);
        else if (cur === "pending" && c.status === "approved")
          statusByChore.set(c.choreId, c.status);
      }
      const todo = myChores.filter((c) => {
        const s = statusByChore.get(c.id);
        return !s || s === "rejected";
      }).length;
      setAppBadge(todo);
      return;
    }
    clearAppBadge();
  }, [
    userDoc,
    isParent,
    isChild,
    uid,
    pendingCompletions.length,
    pendingProposals.length,
    pendingSubmissions.length,
    chores,
    todays,
  ]);
}
